import {
  BookText,
  FileSearch,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Shield,
  Sun,
} from 'lucide-react'
import { NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { useTheme, type Theme } from '@/components/providers/theme-provider'
import { useAuth } from '@/components/providers/auth-provider'
import { useLocale } from '@/components/providers/locale-provider'
import { ROUTE_IDS, useLocalizedPath, type RouteId } from '@/lib/i18n/routes'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * Icons per destination. The nav deliberately looks nothing like the segmented
 * controls elsewhere in the header: those switch between views of one thing
 * (theme, language, term/document/concept mode), while these are separate
 * pages. Same styling for both would tell the user they are the same kind of
 * choice, which they are not.
 *
 * Jobs and Prompts have no icon on purpose. They are debugging aids rather
 * than places curators work, so leaving them as bare text lets the three
 * real destinations carry the visual weight.
 */
const NAV_ICON: Partial<Record<RouteId, typeof BookText>> = {
  termBase: BookText,
  preview: FileSearch,
  settings: Settings,
}

const THEMES: { value: Theme; icon: typeof Sun }[] = [
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
  { value: 'system', icon: Monitor },
]

function ThemeToggle() {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  return (
    <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
      {THEMES.map(({ value, icon: Icon }) => {
        const label = t(`theme.${value}` as const)
        return (
          <Tooltip key={value} label={label}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setTheme(value)}
              aria-label={label}
              aria-pressed={theme === value}
              className={cn(
                'text-muted-foreground',
                theme === value && 'bg-card text-foreground shadow-sm',
              )}
            >
              <Icon />
            </Button>
          </Tooltip>
        )
      })}
    </div>
  )
}

/**
 * Language switcher. Each option is labelled in its own language — someone who
 * has landed in a language they cannot read still needs to find their way out,
 * and "Suomi" is recognisable where a translated "Finnish" would not be.
 */
function LanguageToggle() {
  const { t } = useTranslation()
  const { locale, setLocale, available } = useLocale()
  return (
    <div
      className="flex items-center gap-0.5 rounded-md bg-muted p-0.5"
      role="group"
      aria-label={t('language.label')}
    >
      {available.map((l) => (
        <Tooltip key={l} label={t(`language.${l}` as const)}>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setLocale(l)}
            aria-label={t(`language.${l}` as const)}
            aria-pressed={locale === l}
            lang={l}
            className={cn(
              'text-[10px] font-semibold uppercase text-muted-foreground',
              locale === l && 'bg-card text-foreground shadow-sm',
            )}
          >
            {l}
          </Button>
        </Tooltip>
      ))}
    </div>
  )
}

function UserMenu() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  if (!user) return null
  return (
    <div className="flex items-center gap-2">
      <span className="hidden text-xs text-muted-foreground sm:block">{user.name}</span>
      {user.is_admin && (
        <Badge variant="primary" size="sm" title={t('auth.administrator')}>
          <Shield className="size-2.5" />
          {t('auth.admin')}
        </Badge>
      )}
      <Tooltip label={t('auth.signOut')}>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => logout()}
          aria-label={t('auth.signOut')}
        >
          <LogOut />
        </Button>
      </Tooltip>
    </div>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const path = useLocalizedPath()
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <header className="flex shrink-0 items-center gap-4 border-b border-border px-4 py-2">
        <span className="text-xs font-semibold text-muted-foreground">{t('app.tagline')}</span>
        {/* `self-stretch -my-2` makes the links span the header's full height,
            padding included, so the active underline lands exactly on the
            header's own bottom border rather than floating above it. */}
        <nav className="-my-2 flex items-stretch gap-5 self-stretch">
          {ROUTE_IDS.map((id) => {
            const Icon = NAV_ICON[id]
            return (
              <NavLink
                key={id}
                to={path(id)}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-1.5 text-xs font-medium transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {Icon && <Icon className="size-3.5" />}
                    {t(`nav.${id}` as const)}
                    {isActive && (
                      <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary" />
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <UserMenu />
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>
      <main className="flex min-h-0 flex-1 flex-col">{children}</main>
    </div>
  )
}
